<template>
  <div class="background-color-white d-flex flex-column">
    <div
      class="align-center border-bottom d-flex flex-column justify-center pb-5"
    >
      <div
        v-if="showMinimumOrderError"
        class="color-fire-engine-red e2e-cart-minimum"
      >
        ({{ formatCurrency(storeInfo.min_order_amount) }} Order Minimum)
      </div>
    </div>
    <div v-if="!cart.products || !cart.products.length">
      <EmptyState>Your cart is empty</EmptyState>
    </div>
    <!-- start fixed height for smAndUp -->
    <div v-else class="padding-bottom-space-for-checkout-button">
      <div class="no-overflow e2e-cart-items">
        <div class="d-flex flex-column justify-center">
          <div
            :data-cartitem="cartItem.upc"
            v-for="cartItem in cart.products"
            :key="cartItem.item_id"
            class="border-bottom"
          >
            <CartItem
              :close="
                () => $store.commit('product/CLOSE_DIALOG', 'addFromCart')
              "
              :items="items"
              :modifyCartItems="modifyCartItems"
              :product="cartItem"
              :upc="cartItem.upc"
            >
              <template v-slot:cartQuantityButton="{ cartProduct }">
                <CartQuantityButton
                  :cartProduct="cartProduct"
                  :quantityType="'qty'"
                  small
                />
              </template>
            </CartItem>
          </div>
        </div>
      </div>
    </div>
    <!-- end fixed height for smAndUp -->
    <CheckOutButton
      :totalPriceAfterAdjustments="cart.totalPriceAfterAdjustments"
    />
  </div>
</template>

<script>
let watch = true;
function setWatchToFalse() {
  watch = false;
}

import { mapState } from "vuex";
import {
  formatCurrency,
  updateObjectInArray,
  useSharedLibCart,
} from "@/util.js";
import { calculateCart } from "@/worker-api.js";
import CartItem from "@/components/Product/CartItem.vue";
import CartQuantityButton from "@/components/Product/CartQuantityButton.vue";
import CheckOutButton from "@/components/Cart/CheckOutButton.vue";
import EmptyState from "@/components/EmptyState.vue";
import { DriverTips, StoreTips } from '@/types/tips.js';

export default {
  name: "Cart",
  components: {
    CartItem,
    CartQuantityButton,
    CheckOutButton,
    EmptyState,
  },
  computed: {
    ...mapState({
      cart: (state) => state.cart,
      sharedLibPromos: (state) => state.promotions.sharedLibPromos,
      storeInfo: (state) => state.store.info,
    }),
    showMinimumOrderError() {
      return (
        this.storeInfo?.min_order_amount
        && this.cart.products?.length
        && this.items
        && this.cart.totalPriceAfterAdjustments < this.storeInfo.min_order_amount
      );
    },
  },
  data() {
    return {
      items: null,
      modifiedCartItems: [],
    };
  },
  methods: {
    formatCurrency,
    getEstimatedSubtotal(items) {
      return Object.values(items).reduce((sum, item) => sum + item.priceAfterAdjustments, 0);
    },
    modifyCartItems(cartItem) {
      const has =
        this.modifiedCartItems.length &&
        this.modifiedCartItems.find((_) => _.item_id === cartItem.item_id);
      this.modifiedCartItems = has
        ? updateObjectInArray(this.modifiedCartItems, cartItem, "item_id")
        : [...this.modifiedCartItems, cartItem];
    },
    async setPromotionsPricing() {
      const deliveryAmount = null; // not relevant to cart calculation until checkout

      const sharedLibCart = useSharedLibCart(
        this.cart.products,
        this.sharedLibPromos,
        this.storeInfo.modifierGroups,
        deliveryAmount,
        this.cart.firstOrderDiscount,
        this.storeInfo.fee_buster_config,
        DriverTips.other,
        StoreTips.other,
        this.storeInfo.points_config,
        this.storeInfo.price_increase,
      );
      const { items } = await calculateCart(sharedLibCart);
      this.$store.commit(
        'cart/SET_TOTAL_PRICE_AFTER_ADJUSTMENTS',
        this.getEstimatedSubtotal(items),
      );
      this.items = items;
    },
  },
  mounted() {
    // needed only for mobile
    this.$vuetify.breakpoint.xsOnly && this.setPromotionsPricing();
  },
  watch: {
    "$store.state.cart.products": function () {
      this.sharedLibPromos &&
        this.$route.name !== "checkout" &&
        this.setPromotionsPricing() &&
        watch &&
        setWatchToFalse();
    },
    "$store.state.promotions.sharedLibPromos": function () {
      watch &&
        this.cart?.products?.length &&
        this.setPromotionsPricing() &&
        setWatchToFalse();
    },
  },
};
</script>

<style scoped>
@media (min-width: 668px) {
  .padding-bottom-space-for-checkout-button {
    padding-bottom: 100px;
  }
}
</style>
